import { apiClient } from './apiClient';

export type SalesOpportunityPipelineStage = {
  stage: string;
  count: number;
  totalAmount: number;
  totalMonthlyRecurring: number;
};

export type SalesOpportunityPipeline = {
  stages: SalesOpportunityPipelineStage[];
  pipelineValue: number;
  totalCount: number;
};

export type SalesOpportunityTask = {
  id: string;
  salesOpportunityId: string;
  title: string;
  description?: string | null;
  assignedToUserId: string;
  assignedToUserName?: string | null;
  dueAtUtc: string;
  status: string;
  notifyInApp: boolean;
  notifyByEmail: boolean;
  reminderMode: string;
  reminderOffsetMinutes?: number | null;
  reminderSpecificAtUtc?: string | null;
  notificationMessageTemplate: string;
  reminderSentAtUtc?: string | null;
  completedAtUtc?: string | null;
  recurrenceSeriesId?: string | null;
  recurrenceType?: string;
  recurrenceDaysOfWeek?: string | null;
  recurrenceUntilDate?: string | null;
  recurrenceIndefinite?: boolean;
  recurrenceActive?: boolean;
};

export type SalesOpportunityPricingLine = {
  id: string;
  nature: string;
  amount: number;
  salesProductId?: string | null;
  salesProductName?: string | null;
  notes?: string | null;
  sortOrder: number;
};

export type SalesOpportunity = {
  id: string;
  title: string;
  description?: string | null;
  clientName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  marketSegmentId?: string | null;
  marketSegmentName?: string | null;
  projectName?: string | null;
  salesProductId?: string | null;
  salesProductName?: string | null;
  prospectId?: string | null;
  prospectName?: string | null;
  prospectCompany?: string | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  amount: number;
  monthlyRecurringAmount: number;
  revenueType: string;
  stage: string;
  engagementStatus: string;
  probabilityPercent: number;
  sortOrder: number;
  expectedCloseDateUtc?: string | null;
  nextContactAtUtc?: string | null;
  closedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  pricingLines: SalesOpportunityPricingLine[];
  tasks: SalesOpportunityTask[];
};

export type SalesOpportunityListResult = {
  pipeline: SalesOpportunityPipeline;
  items: SalesOpportunity[];
};

export type SalesOpportunityPricingLineUpsert = {
  nature: string;
  amount: number;
  salesProductId?: string;
  notes?: string;
  sortOrder?: number;
};

export type SalesOpportunityUpsertPayload = {
  title: string;
  description?: string;
  clientName?: string;
  companyId?: string;
  marketSegmentId?: string;
  projectName?: string;
  salesProductId?: string;
  prospectId?: string;
  ownerUserId?: string;
  amount: number;
  monthlyRecurringAmount: number;
  revenueType: string;
  stage: string;
  engagementStatus?: string;
  probabilityPercent: number;
  expectedCloseDateUtc?: string;
  nextContactAtUtc?: string;
  pricingLines?: SalesOpportunityPricingLineUpsert[];
};

export type SalesOpportunityStageUpdatePayload = {
  stage: string;
  sortOrder?: number;
};

export type SalesOpportunityTaskUpsertPayload = {
  title: string;
  description?: string;
  assignedToUserId: string;
  dueAtUtc: string;
  notifyInApp: boolean;
  notifyByEmail: boolean;
  reminderMode: string;
  reminderOffsetMinutes?: number;
  reminderSpecificAtUtc?: string;
  notificationMessageTemplate: string;
  recurrenceType?: string;
  recurrenceDaysOfWeek?: string;
  recurrenceUntilDate?: string;
  recurrenceIndefinite?: boolean;
  scheduleOffsetMinutes?: number;
};

export type SalesOpportunityTeamMember = {
  id: string;
  name: string;
  email: string;
};

export async function listSalesOpportunities(): Promise<SalesOpportunityListResult> {
  const { data } = await apiClient.get<SalesOpportunityListResult>('/v1/sales/opportunities');
  return data;
}

export async function syncSalesOpportunities(): Promise<SalesOpportunityListResult> {
  const { data } = await apiClient.post<SalesOpportunityListResult>('/v1/sales/opportunities/sync');
  return data;
}

export async function getSalesOpportunity(id: string): Promise<SalesOpportunity> {
  const { data } = await apiClient.get<SalesOpportunity>(`/v1/sales/opportunities/${id}`);
  return data;
}

export async function createSalesOpportunity(
  payload: SalesOpportunityUpsertPayload,
): Promise<SalesOpportunity> {
  const { data } = await apiClient.post<SalesOpportunity>('/v1/sales/opportunities', payload);
  return data;
}

export async function updateSalesOpportunity(
  id: string,
  payload: SalesOpportunityUpsertPayload,
): Promise<SalesOpportunity> {
  const { data } = await apiClient.put<SalesOpportunity>(`/v1/sales/opportunities/${id}`, payload);
  return data;
}

export async function updateSalesOpportunityStage(
  id: string,
  payload: SalesOpportunityStageUpdatePayload,
): Promise<SalesOpportunity> {
  const { data } = await apiClient.patch<SalesOpportunity>(
    `/v1/sales/opportunities/${id}/stage`,
    payload,
  );
  return data;
}

export async function deleteSalesOpportunity(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/opportunities/${id}`);
}

export async function listOpportunityTeamMembers(): Promise<SalesOpportunityTeamMember[]> {
  const { data } = await apiClient.get<SalesOpportunityTeamMember[]>(
    '/v1/sales/opportunities/team-members',
  );
  return data;
}

export async function createOpportunityTask(
  opportunityId: string,
  payload: SalesOpportunityTaskUpsertPayload,
): Promise<SalesOpportunityTask> {
  const { data } = await apiClient.post<SalesOpportunityTask>(
    `/v1/sales/opportunities/${opportunityId}/tasks`,
    payload,
  );
  return data;
}

export async function updateOpportunityTask(
  taskId: string,
  payload: SalesOpportunityTaskUpsertPayload,
): Promise<SalesOpportunityTask> {
  const { data } = await apiClient.put<SalesOpportunityTask>(
    `/v1/sales/opportunities/tasks/${taskId}`,
    payload,
  );
  return data;
}

export async function completeOpportunityTask(taskId: string): Promise<SalesOpportunityTask> {
  const { data } = await apiClient.post<SalesOpportunityTask>(
    `/v1/sales/opportunities/tasks/${taskId}/complete`,
  );
  return data;
}

export async function deleteOpportunityTask(taskId: string): Promise<void> {
  await apiClient.delete(`/v1/sales/opportunities/tasks/${taskId}`);
}

export async function stopOpportunityTaskRecurrence(taskId: string): Promise<void> {
  await apiClient.post(`/v1/sales/opportunities/tasks/${taskId}/stop-recurrence`);
}

export type SalesOpportunityImportQueued = {
  importId: string;
  hangfireJobId?: string | null;
  status: string;
  message: string;
};

export type SalesOpportunityImportStatus = {
  id: string;
  originalFileName: string;
  status: string;
  hangfireJobId?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  companiesCreatedCount: number;
  errorSummary?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
};

export async function downloadOpportunityImportTemplate(): Promise<Blob> {
  const response = await apiClient.get('/v1/sales/opportunities/import/template', {
    responseType: 'blob',
  });
  return response.data;
}

export async function importSalesOpportunities(file: File): Promise<SalesOpportunityImportQueued> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<SalesOpportunityImportQueued>(
    '/v1/sales/opportunities/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getSalesOpportunityImportStatus(
  importId: string,
): Promise<SalesOpportunityImportStatus> {
  const { data } = await apiClient.get<SalesOpportunityImportStatus>(
    `/v1/sales/opportunities/import/${importId}`,
  );
  return data;
}

export type SalesOpportunityConvertFromProspectPayload = {
  prospectId: string;
  title?: string;
  salesProductId?: string;
  ownerUserId?: string;
  amount: number;
  monthlyRecurringAmount: number;
  revenueType: string;
  stage: string;
  probabilityPercent: number;
  expectedCloseDateUtc?: string;
};

export async function convertProspectToOpportunity(
  payload: SalesOpportunityConvertFromProspectPayload,
): Promise<SalesOpportunity> {
  const { data } = await apiClient.post<SalesOpportunity>(
    '/v1/sales/opportunities/convert-from-prospect',
    payload,
  );
  return data;
}

export const DEFAULT_TASK_NOTIFICATION_TEMPLATE =
  'Lembrete: {{tarefa.titulo}} — {{oportunidade.titulo}} ({{prospect.empresa}}). Vencimento: {{tarefa.vencimento}}. Responsável: {{responsavel.nome}}.';

export const TASK_MACRO_HINTS = [
  '{{oportunidade.titulo}}',
  '{{oportunidade.valor}}',
  '{{prospect.nome}}',
  '{{prospect.empresa}}',
  '{{prospect.email}}',
  '{{responsavel.nome}}',
  '{{tarefa.titulo}}',
  '{{tarefa.vencimento}}',
];
